import type { LogicAction, LogicCondition, LogicDocument, LogicEventKind, LogicRule, QuestStatus } from './contract';

// One concrete occurrence a rule can react to. Carries the event's own facts
// (team, player, trigger) alongside the tick it happened on. `team` is a
// plain string rather than a game-specific union so this stays usable by any
// host - a two-team shooter, a solo exploration game, anything in between.
export interface LogicEvent {
  kind: LogicEventKind;
  tick: number;
  team?: string;
  playerId?: string;
  trigger?: string;
}

// The facts conditions are tested against, snapshotted by the caller at the
// tick the event fires. `scores` keeps the two-slot shape the original
// contract's `score-raiders`/`score-wardens` facts read from - a host with no
// real team scoring (the generic client-side runtime, for a single-player
// project) reports zero for both rather than inventing a meaning for them.
export interface LogicFacts {
  round: number;
  scores: { raiders: number; wardens: number };
  playerCount: number;
}

// What a fired rule asks the host to do. Announces are broadcast/displayed;
// emits land in the effect log for a host to react to further. Variable
// actions mutate the evaluator's own deterministic state and produce no
// effect. Item actions mutate per-player item state and log an 'item' effect
// carrying the new count, so a HUD or audit trail can follow inventory
// changes without re-deriving them. 'win'/'lose'/'quest' mutate the
// evaluator's own outcome/quest state (see `outcome()`/`questStatus()`)
// exactly like item actions mutate item state, and log an effect for the
// same reason: a host reacts to the moment it happened without re-deriving
// it from polled state every frame.
export type LogicEffect =
  | { kind: 'announce'; tick: number; ruleId: string; message: string }
  | { kind: 'emit'; tick: number; ruleId: string; event: string }
  | { kind: 'item'; tick: number; ruleId: string; playerId: string; item: string; delta: number; count: number }
  | { kind: 'win'; tick: number; ruleId: string; team?: string }
  | { kind: 'lose'; tick: number; ruleId: string; team?: string }
  | { kind: 'quest'; tick: number; ruleId: string; questId: string; title?: string; status: QuestStatus };

/**
 * Evaluates a parsed logic document, deterministically: rules run in
 * document order, conditions AND together, and all state (per-match
 * variables, per-player items, once-tracking) lives inside the evaluator,
 * fed only by events and facts. The same event sequence always produces the
 * same effect sequence.
 *
 * This is the one rule-matching engine every host shares - the generic
 * client-side play/preview runtime and sstk-sunder-realtime's authoritative
 * match server both construct one of these against the same
 * `LogicDocument` and feed it their own events/facts. Only how events and
 * facts are derived differs per host (a snapshot-diffing driver for an
 * authoritative simulation, direct trigger-containment + tick events for a
 * local single-player session) - this class itself carries no assumption
 * about either.
 */
export class LogicEvaluator {
  private readonly rules: readonly LogicRule[];
  private readonly variables = new Map<string, number>();
  private readonly fired = new Set<string>();
  // Per-player item counts. Keyed playerId -> item name -> count, fed only by
  // give-item/take-item actions, so it stays deterministic.
  private readonly items = new Map<string, Map<string, number>>();
  // Keyed questId -> {title, status}. Absent means 'inactive' (never
  // started) - see `questStatus`.
  private readonly quests = new Map<string, { title?: string; status: QuestStatus }>();
  // The session's outcome once a win-game/lose-game action has fired;
  // whichever fires first wins (a rule ending the session a second way is a
  // no-op, same "first one wins" posture `once`-guarded rules already give
  // an author for free, but this holds even without `once` since a
  // finished session has nothing left to decide).
  private outcomeState: { result: 'win' | 'lose'; team?: string } | null = null;

  constructor(document: LogicDocument) {
    this.rules = document.rules;
  }

  /** Current value of a variable; unset variables read as 0. */
  variable(name: string): number {
    return this.variables.get(name) ?? 0;
  }

  /** A player's count of an item; players and items never seen read as 0. */
  itemCount(playerId: string, name: string): number {
    return this.items.get(playerId)?.get(name) ?? 0;
  }

  /** A named quest's current status; a quest never started reads as
   * 'inactive', matching `quest-status` condition semantics exactly. */
  questStatus(questId: string): QuestStatus {
    return this.quests.get(questId)?.status ?? 'inactive';
  }

  /** The session's outcome once a `win-game`/`lose-game` action has fired,
   * else null (still in progress). */
  outcome(): { result: 'win' | 'lose'; team?: string } | null {
    return this.outcomeState;
  }

  /** Every variable this evaluator currently holds a value for, by name -
   * for a multiplayer Logic host broadcasting a full `logic-snapshot`
   * (docs/multiplayer-sync/roadmap.md section 3.4): `variable(name)` alone
   * can only answer "what is X", never "what variables even exist", and a
   * snapshot must carry the creator's *actual* current variable set, not a
   * guessed/hardcoded subset. A plain copy, not a live view - mutating the
   * returned object never touches this evaluator's own state. */
  allVariables(): Record<string, number> {
    return Object.fromEntries(this.variables);
  }

  /** Every quest this evaluator has any record of (i.e. has been started,
   * completed, or failed at least once), by id - the `allVariables`
   * counterpart for quests, same reasoning: a `logic-snapshot` needs the
   * real current quest set, not a guess. A quest never explicitly
   * interacted with is correctly absent here (its status still reads
   * 'inactive' via `questStatus`, same as before this method existed) -
   * there is nothing meaningful to snapshot for a quest that has never
   * happened. */
  allQuestStatuses(): Record<string, { status: QuestStatus; title?: string }> {
    const out: Record<string, { status: QuestStatus; title?: string }> = {};
    for (const [questId, quest] of this.quests) {
      out[questId] = quest.title !== undefined ? { status: quest.status, title: quest.title } : { status: quest.status };
    }
    return out;
  }

  /** A named quest's current display title, or `undefined` if it was never
   * given one (a quest moved straight to complete/fail with no prior
   * `start-quest`, or restored from a `logic-snapshot` that itself carried
   * no title) - the `variable`/`itemCount` counterpart for a quest's title,
   * so a caller (a host's own quest-log UI, or a test) can read back what
   * `setQuestStatus`/`recordQuestEffect` actually stored, including a title
   * that only ever arrived via a multiplayer snapshot restore rather than a
   * locally-evaluated `start-quest` rule. */
  questTitle(questId: string): string | undefined {
    return this.quests.get(questId)?.title;
  }

  /** Every player's item counts this evaluator currently holds, by player id
   * then item name - the `allVariables`/`allQuestStatuses` counterpart for
   * items, for a multiplayer Logic host's `logic-snapshot` broadcast: item
   * counts are otherwise absent from that snapshot entirely (unlike
   * variables and quests), so a late joiner never catches up on them. A
   * plain copy, not a live view. */
  allItemCounts(): Record<string, Record<string, number>> {
    const out: Record<string, Record<string, number>> = {};
    for (const [playerId, owned] of this.items) {
      out[playerId] = Object.fromEntries(owned);
    }
    return out;
  }

  /** Directly set a variable's value, bypassing rule matching entirely - for
   * a host capability (e.g. a HollowScript `variables.set`/`variables.add`
   * call) that needs to write without authoring a synthetic rule for it.
   * Produces no effect, matching `set-variable`/`add-variable`'s own
   * behaviour inside `evaluate()`. */
  setVariable(name: string, value: number): void {
    this.variables.set(name, value);
  }

  /** Directly set a quest's status (and, when known, its title), bypassing
   * rule matching entirely - the `setVariable` counterpart for quests, for a
   * multiplayer non-host client restoring a `logic-snapshot`'s `quests` map
   * (docs/multiplayer-sync/roadmap.md section 3.4). A snapshot's own
   * `quests` entries *do* carry a `title` alongside `status`
   * (`LogicSnapshotSyncMessage`, syncMessage.ts - carried specifically so a
   * late joiner does not lose it) - passed through here as `title`; an
   * omitted/`undefined` title keeps whatever the quest already had locally
   * rather than clearing it, so a resync never regresses a title this
   * client already knew from its own earlier `start-quest` effect. Produces
   * no effect, same reasoning as `setVariable`: a resync is silent, not a
   * narrated event. */
  setQuestStatus(questId: string, status: QuestStatus, title?: string): void {
    const existing = this.quests.get(questId);
    this.quests.set(questId, { title: title ?? existing?.title, status });
  }

  /** Directly set one player's count of an item, bypassing rule matching
   * entirely - the `setVariable` counterpart for items, for a multiplayer
   * non-host client restoring a `logic-snapshot`'s `items` map (added
   * alongside `variables`/`quests` so item counts also catch a late joiner
   * up, rather than being silently absent from the resync mechanism).
   * Produces no effect, same reasoning as `setVariable`. */
  setItemCount(playerId: string, name: string, count: number): void {
    let owned = this.items.get(playerId);
    if (!owned) {
      owned = new Map();
      this.items.set(playerId, owned);
    }
    owned.set(name, count);
  }

  /** Directly set the session's outcome, bypassing rule matching (and its
   * "first result wins" gate - see `executeAction`'s `win-game`/`lose-game`
   * case) - the `setVariable` counterpart for outcome, for a multiplayer
   * non-host client restoring a `logic-snapshot`'s `outcome` field. Passing
   * `null` clears a previously-set outcome, which real gameplay can never do
   * (a match never un-ends) but a stale restore legitimately can: a client
   * that raced ahead with its own now-superseded local outcome must be able
   * to un-set it when the host's authoritative snapshot says the match is
   * still in progress. Produces no effect, same reasoning as `setVariable`. */
  setOutcome(outcome: { result: 'win' | 'lose'; team?: string } | null): void {
    this.outcomeState = outcome;
  }

  /**
   * Execute one action directly, outside of rule matching - for a host
   * capability that wants to produce the exact same effect an authored
   * rule's action would, without wrapping it in a synthetic rule/event. Runs
   * through the same `executeAction` a matched rule's `then` list uses, so a
   * script-triggered `logic.announce(...)` and a rule-authored `announce`
   * action are indistinguishable to anything downstream (the HUD feed,
   * `log`). `tick`/`playerId` are threaded into the synthetic event the same
   * way `evaluate()`'s own event is built; `ruleId` labels the resulting
   * effect the same way a real rule's id would.
   */
  applyAction(action: LogicAction, tick: number, ruleId: string, playerId?: string): LogicEffect | undefined {
    const event: LogicEvent = { kind: 'tick', tick, playerId };
    return this.executeAction(action, ruleId, event, this.facts());
  }

  /** The zero-defaults every direct (non-rule) fact read gets - a script
   * calling a `logic` capability has no round/score context to supply,
   * exactly like the generic client-side host's own `facts()` (see
   * `ClientLogicRuntime`). */
  private facts(): LogicFacts {
    return { round: 1, scores: { raiders: 0, wardens: 0 }, playerCount: 1 };
  }

  private adjustItem(playerId: string, name: string, delta: number): number {
    let owned = this.items.get(playerId);
    if (!owned) {
      owned = new Map();
      this.items.set(playerId, owned);
    }
    const next = Math.max(0, (owned.get(name) ?? 0) + delta);
    owned.set(name, next);
    return next;
  }

  /** Move a quest to `status`, defaulting its title from whatever it was
   * last known as (start-quest's own title, or undefined if this quest was
   * never explicitly started - `complete-quest`/`fail-quest` do not require
   * one). Returns the effect to log - the rule-matching/`applyAction` path;
   * see the public `setQuestStatus` for the direct, no-effect counterpart a
   * multiplayer resync uses. */
  private recordQuestEffect(
    questId: string,
    status: QuestStatus,
    tick: number,
    ruleId: string,
    title?: string,
  ): LogicEffect {
    const existing = this.quests.get(questId);
    const resolvedTitle = title ?? existing?.title;
    this.quests.set(questId, { title: resolvedTitle, status });
    return { kind: 'quest', tick, ruleId, questId, status, ...(resolvedTitle ? { title: resolvedTitle } : {}) };
  }

  evaluate(event: LogicEvent, facts: LogicFacts): LogicEffect[] {
    const effects: LogicEffect[] = [];
    for (const rule of this.rules) {
      if (rule.when !== event.kind) continue;
      if (rule.enabled === false) continue;
      if (rule.once && this.fired.has(rule.id)) continue;
      if (rule.if && !rule.if.every((condition) => this.holds(condition, event, facts))) continue;

      this.fired.add(rule.id);
      for (const action of rule.then) {
        const effect = this.executeAction(action, rule.id, event, facts);
        if (effect) effects.push(effect);
      }
    }
    return effects;
  }

  /** Runs one action, mutating whatever state it owns (variables/items/
   * quests/outcome) and returning the effect to log, or undefined for an
   * action kind that produces none (`set-variable`/`add-variable`, an
   * outcome action once the session already ended, `play-cutscene`).
   * Extracted so `evaluate()`'s per-rule loop and `applyAction()`'s direct,
   * rule-free path share exactly one execution path - a script-triggered
   * `logic.announce(...)` and a rule-authored `announce` action must be
   * indistinguishable to anything downstream. */
  private executeAction(action: LogicAction, ruleId: string, event: LogicEvent, facts: LogicFacts): LogicEffect | undefined {
    switch (action.kind) {
      case 'announce':
        return { kind: 'announce', tick: event.tick, ruleId, message: substitute(action.message, event, facts) };
      case 'set-variable':
        this.variables.set(action.name, action.value);
        return undefined;
      case 'add-variable':
        this.variables.set(action.name, this.variable(action.name) + action.value);
        return undefined;
      case 'emit':
        return { kind: 'emit', tick: event.tick, ruleId, event: substitute(action.event, event, facts) };
      case 'give-item':
      case 'take-item': {
        // Item actions target the event's player; an event with no player
        // context (round start/end) has no one to give to, so they skip.
        if (!event.playerId) return undefined;
        const delta = action.kind === 'give-item' ? action.count : -action.count;
        const count = this.adjustItem(event.playerId, action.name, delta);
        return { kind: 'item', tick: event.tick, ruleId, playerId: event.playerId, item: action.name, delta, count };
      }
      case 'play-cutscene':
        // No runtime executes this action kind yet (see
        // isActionKindUnexecuted in contract.ts) - authoring it is valid,
        // running it is not, so it deliberately produces no effect rather
        // than a silently-wrong one.
        return undefined;
      case 'win-game':
      case 'lose-game': {
        // First outcome wins - a session that already ended has nothing
        // left to decide, so a later win-game/lose-game (e.g. two
        // independent rules both matching the same event, or a script
        // calling logic.winGame() after a rule already ended it) is a
        // no-op rather than overwriting a real result.
        if (this.outcomeState) return undefined;
        const result = action.kind === 'win-game' ? 'win' : 'lose';
        this.outcomeState = { result, team: action.team };
        return { kind: result, tick: event.tick, ruleId, ...(action.team ? { team: action.team } : {}) };
      }
      case 'start-quest':
        return this.recordQuestEffect(action.questId, 'active', event.tick, ruleId, action.title);
      case 'complete-quest':
        return this.recordQuestEffect(action.questId, 'complete', event.tick, ruleId);
      case 'fail-quest':
        return this.recordQuestEffect(action.questId, 'failed', event.tick, ruleId);
    }
  }

  private holds(condition: LogicCondition, event: LogicEvent, facts: LogicFacts): boolean {
    const actual = this.factValue(condition, event, facts);
    if (actual === undefined) return false;
    return compare(actual, condition.op, condition.value);
  }

  private factValue(
    condition: LogicCondition,
    event: LogicEvent,
    facts: LogicFacts,
  ): number | string | undefined {
    switch (condition.fact) {
      case 'round':
        return facts.round;
      case 'score-raiders':
        return facts.scores.raiders;
      case 'score-wardens':
        return facts.scores.wardens;
      case 'player-count':
        return facts.playerCount;
      case 'event-team':
        return event.team;
      case 'event-trigger':
        return event.trigger;
      case 'event-player':
        return event.playerId;
      case 'variable':
        return this.variable(condition.name ?? '');
      case 'item-count':
        // Counted for the event's player; events without one never hold.
        return event.playerId === undefined
          ? undefined
          : this.itemCount(event.playerId, condition.name ?? '');
      case 'quest-status':
        return this.questStatus(condition.name ?? '');
    }
  }
}

// Numeric facts support the full comparison set; strings only equality, so a
// malformed ordering comparison on a string fact simply never holds.
function compare(actual: number | string, op: string, expected: number | string): boolean {
  if (op === 'eq') return actual === expected;
  if (op === 'ne') return actual !== expected;
  if (typeof actual !== 'number' || typeof expected !== 'number') return false;
  switch (op) {
    case 'lt':
      return actual < expected;
    case 'le':
      return actual <= expected;
    case 'gt':
      return actual > expected;
    case 'ge':
      return actual >= expected;
    default:
      return false;
  }
}

// Announce messages and emitted event names substitute the event's facts:
// {team}, {player}, {trigger}, {round}, and {score-raiders}/{score-wardens}.
// Unknown placeholders pass through unchanged so a typo is visible rather
// than silent.
function substitute(message: string, event: LogicEvent, facts: LogicFacts): string {
  return message.replace(/\{([a-z-]+)\}/g, (whole, key: string) => {
    switch (key) {
      case 'team':
        return event.team ?? whole;
      case 'player':
        return event.playerId ?? whole;
      case 'trigger':
        return event.trigger ?? whole;
      case 'round':
        return String(facts.round);
      case 'score-raiders':
        return String(facts.scores.raiders);
      case 'score-wardens':
        return String(facts.scores.wardens);
      default:
        return whole;
    }
  });
}

/**
 * The inverse of `executeAction`: turns a produced `LogicEffect` back into
 * the `LogicAction` that would produce an equivalent one, for a multiplayer
 * non-host client replaying a host's broadcast `logic-effect` message
 * (docs/multiplayer-sync/roadmap.md section 3.2 item 3) via
 * `ClientLogicRuntime.applyRemoteAction` - the same "apply an effect the
 * same way a script does, under a reserved ruleId" bypass-rule-matching
 * path Phase 0's `'__remote'` extension point was built for, now with
 * something to actually call it. `null` for an effect kind with no action
 * that reproduces it: `'quest'` at `'inactive'` (no action ever moves a
 * quest back to inactive - `QUEST_STATUSES`' own ordering in contract.ts
 * says a quest never un-starts, so no `LogicEffect` this evaluator itself
 * produces ever carries that status either; kept here only so the switch is
 * exhaustive over every real `QuestStatus`, not because a real effect can
 * reach it).
 */
export function logicEffectToAction(effect: LogicEffect): LogicAction | null {
  switch (effect.kind) {
    case 'announce':
      return { kind: 'announce', message: effect.message };
    case 'emit':
      return { kind: 'emit', event: effect.event };
    case 'item':
      return effect.delta >= 0
        ? { kind: 'give-item', name: effect.item, count: effect.delta }
        : { kind: 'take-item', name: effect.item, count: -effect.delta };
    case 'win':
      return { kind: 'win-game', ...(effect.team ? { team: effect.team } : {}) };
    case 'lose':
      return { kind: 'lose-game', ...(effect.team ? { team: effect.team } : {}) };
    case 'quest':
      switch (effect.status) {
        case 'active':
          return { kind: 'start-quest', questId: effect.questId, title: effect.title ?? effect.questId };
        case 'complete':
          return { kind: 'complete-quest', questId: effect.questId };
        case 'failed':
          return { kind: 'fail-quest', questId: effect.questId };
        case 'inactive':
          return null;
      }
  }
}

/**
 * The real acting player an effect should be attributed to when a non-host
 * client replays it via `applyRemoteAction` - `undefined` when the effect
 * carries no per-player owner of its own, in which case a caller should fall
 * back to the relay message's sender id instead (roadmap.md section 3.2's
 * "a host-driven NPC/prop it owns" case, or a shared-lifecycle effect like
 * `round-start`/`tick`, both stamped with whichever player the broadcasting
 * host itself is).
 *
 * Only `'item'` effects carry a genuine per-player owner today
 * (`LogicEffect`'s own `playerId` field) - and only that field, never the
 * message's sender, is correct once an effect can originate from something
 * other than the host's own player. This distinction was unreachable while
 * only round-start/tick effects ever synced (both always stamped with the
 * broadcasting host's own local player id, so sender and origin always
 * happened to coincide), but once trigger-sourced (docs/multiplayer-sync/
 * roadmap.md's Phase 5 honesty pass) and script-triggered effects sync too,
 * an item effect can genuinely belong to a different player than whichever
 * client relayed it - defaulting to the sender id there would misattribute
 * the pickup to the host instead of whoever actually earned it.
 */
export function logicEffectOriginPlayerId(effect: LogicEffect): string | undefined {
  return effect.kind === 'item' ? effect.playerId : undefined;
}
